// src/features/hotels/components/HotelActions.tsx

import React, { useState } from 'react';
import {
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  useRedirect,
} from 'react-admin';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  Visibility,
  BarChart,
  DeleteForever,
} from '@mui/icons-material';
import { useHotelPermissions } from '../hooks/useHotelPermissions';
import type { Hotel } from '../types';

interface HotelActionsProps {
  record: Hotel;
}

/**
 * Row actions for Hotels list
 * Shows menu with Edit, View, Stats, Delete and Force Delete options based on permissions
 */
const HotelActions: React.FC<HotelActionsProps> = ({ record }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const { canEdit, canDelete, canShow, canViewStats, canForceDelete } = useHotelPermissions();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [forceDeleteDialogOpen, setForceDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [forceDeleteResult, setForceDeleteResult] = useState<any>(null);

  const open = Boolean(anchorEl);

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  // Action handlers
  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    redirect('edit', 'hotels', record.id);
  };

  const handleShow = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    redirect('show', 'hotels', record.id);
  };

  const handleViewStats = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    notify('Stats view coming soon', { type: 'info' });
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    setDeleteDialogOpen(true);
  };

  const handleForceDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    setForceDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleForceDeleteDialogClose = () => {
    setForceDeleteDialogOpen(false);
    setForceDeleteResult(null);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      await dataProvider.delete('hotels', {
        id: record.id,
        previousData: record,
      });

      notify('resources.hotels.messages.delete_success', { type: 'success' });
      handleDeleteDialogClose();
      refresh();
    } catch (error: any) {
      const errorCode = error?.body?.error?.code;
      let errorMessage = translate('resources.hotels.messages.delete_error');

      if (errorCode === 'HOTEL_HAS_DEVICES') {
        errorMessage = translate('resources.hotels.messages.delete_with_devices');
      } else if (errorCode === 'HOTEL_HAS_RECENT_ACTIVITY') {
        errorMessage = translate('resources.hotels.messages.delete_with_checkins');
      } else if (error?.body?.error?.message) {
        errorMessage = error.body.error.message;
      }

      notify(errorMessage, { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleForceDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      const result = await dataProvider.forceDeleteHotel(record.id);
      setForceDeleteResult(result.data);
      notify('resources.hotels.messages.force_delete_success', { type: 'success' });
      refresh();
    } catch (error: any) {
      const errorMessage = error?.message || translate('resources.hotels.messages.force_delete_error');
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if hotel has devices or recent activity
  const hasDevices = record.deviceCount > 0;
  const canDeleteHotel = canDelete && !hasDevices;

  // Determine if any menu items would be visible
  const hasAnyAction = canShow || canViewStats || canEdit || canDelete || canForceDelete;

  // Don't render anything if no actions are available
  if (!hasAnyAction) return null;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={translate('ra.action.menu')}>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ color: 'action.active' }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        id="hotel-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={(e: any) => handleClose(e)}
        onClick={(e) => e.stopPropagation()}
        MenuListProps={{
          'aria-labelledby': 'hotel-actions-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* View */}
        {canShow && (
          <MenuItem onClick={handleShow}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ra.action.show')}</ListItemText>
          </MenuItem>
        )}

        {/* View Stats */}
        {canViewStats && (
          <MenuItem onClick={handleViewStats}>
            <ListItemIcon>
              <BarChart fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('resources.hotels.actions.viewStats')}</ListItemText>
          </MenuItem>
        )}

        {/* Edit */}
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ra.action.edit')}</ListItemText>
          </MenuItem>
        )}

        {/* Delete */}
        {canDelete && (
          <MenuItem
            onClick={handleDeleteClick}
            disabled={!canDeleteHotel}
            sx={{
              color: canDeleteHotel ? 'error.main' : 'text.disabled',
              '&:hover': {
                backgroundColor: canDeleteHotel ? 'error.light' : 'transparent',
              },
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color={canDeleteHotel ? 'error' : 'disabled'} />
            </ListItemIcon>
            <ListItemText>
              {translate('ra.action.delete')}
              {hasDevices && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {translate('resources.hotels.messages.delete_with_devices')}
                </Typography>
              )}
            </ListItemText>
          </MenuItem>
        )}

        {/* Force Delete - SuperAdmin only */}
        {canForceDelete && hasDevices && (
          <>
            <Divider />
            <MenuItem
              onClick={handleForceDeleteClick}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light',
                },
              }}
            >
              <ListItemIcon>
                <DeleteForever fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>
                {translate('resources.hotels.actions.forceDelete')}
                <Typography variant="caption" display="block" color="text.secondary">
                  {translate('resources.hotels.messages.force_delete_warning_short')}
                </Typography>
              </ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          {translate('resources.hotels.messages.delete_confirm')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate('ra.message.delete_content', {
              name: record.name,
            })}
          </DialogContentText>

          {hasDevices && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {translate('resources.hotels.messages.delete_with_devices')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={isDeleting}>
            {translate('ra.action.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting || hasDevices}
            autoFocus
          >
            {isDeleting
              ? translate('ra.action.loading')
              : translate('ra.action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Delete Confirmation Dialog */}
      <Dialog
        open={forceDeleteDialogOpen}
        onClose={handleForceDeleteDialogClose}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteForever />
            {translate('resources.hotels.messages.force_delete_confirm')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {!forceDeleteResult ? (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                {translate('resources.hotels.messages.force_delete_warning')}
              </Alert>

              <Typography variant="body2" gutterBottom>
                {translate('resources.hotels.messages.force_delete_description', {
                  name: record.name,
                })}
              </Typography>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {translate('resources.hotels.messages.force_delete_will_remove')}:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2 }}>
                  <li>{translate('resources.hotels.messages.force_delete_item_devices', { count: record.deviceCount })}</li>
                  <li>{translate('resources.hotels.messages.force_delete_item_journeys')}</li>
                  <li>{translate('resources.hotels.messages.force_delete_item_events')}</li>
                  <li>{translate('resources.hotels.messages.force_delete_item_config')}</li>
                </Typography>
              </Box>
            </>
          ) : (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                {translate('resources.hotels.messages.force_delete_completed')}
              </Alert>

              <Typography variant="subtitle2" gutterBottom>
                {translate('resources.hotels.messages.force_delete_summary')}:
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2">
                  • {translate('resources.hotels.messages.deleted_devices')}: {forceDeleteResult.deletedDevices || 0}
                </Typography>
                <Typography variant="body2">
                  • {translate('resources.hotels.messages.deleted_journeys')}: {forceDeleteResult.deletedJourneys || 0}
                </Typography>
                <Typography variant="body2">
                  • {translate('resources.hotels.messages.deleted_events')}: {forceDeleteResult.deletedEvents || 0}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!forceDeleteResult ? (
            <>
              <Button onClick={handleForceDeleteDialogClose} disabled={isDeleting}>
                {translate('ra.action.cancel')}
              </Button>
              <Button
                onClick={handleForceDeleteConfirm}
                color="error"
                variant="contained"
                disabled={isDeleting}
                startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteForever />}
              >
                {isDeleting
                  ? translate('ra.action.loading')
                  : translate('resources.hotels.actions.confirmForceDelete')}
              </Button>
            </>
          ) : (
            <Button onClick={handleForceDeleteDialogClose} variant="contained">
              {translate('ra.action.close')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HotelActions;
